import { DASHBOARD_ORIGIN } from "@/config/studio-env";
import { isEmbedded } from ".";

/**
 * Say so at boot when this build was made for a different dashboard.
 *
 * `VITE_DASHBOARD_ORIGIN` is baked in at build time and is BOTH the target of
 * every outbound postMessage and the allow-list for every inbound one. Point a
 * build at the wrong environment and nothing works, in both directions — but
 * nothing says why:
 *
 *   · every send is refused by the browser with "The target origin provided
 *     ('https://dev.…') does not match the recipient window's origin
 *     ('https://stg.…')", repeated per message;
 *   · the token request is one of those sends, so the host never hears it,
 *     never replies, and the Studio reports "Timed out waiting for host to
 *     respond with an editor token" — which blames a slow host for a message
 *     it was never handed.
 *
 * That is what staging shipped as (dashboard team, 2026-08-25), and
 * `studio-env.ts` could not catch it: it throws for a MISSING or MALFORMED
 * origin, and `https://dev.storemandu.com` is neither. It is well-formed and
 * wrong, which only the running page can know.
 *
 * Deliberately a log, not a throw:
 *
 *   · `document.referrer` is empty under `no-referrer` / `same-origin`
 *     policies and after some navigations, so a throw would take down a
 *     correctly-configured Studio on a host we cannot see;
 *   · a same-origin-policy detail should not be fatal — the app still renders
 *     its own terminal states, and a developer needs the console to read them.
 *
 * Standalone is silent: there is no host to disagree with.
 */
export function warnIfHostOriginDiffers(): void {
  if (!isEmbedded()) return;

  // Empty under a restrictive referrer policy — indistinguishable from "no
  // host", so it cannot be evidence of anything.
  const referrer = document.referrer;
  if (!referrer) return;

  let embeddedIn: string;
  try {
    embeddedIn = new URL(referrer).origin;
  } catch {
    return;
  }

  if (embeddedIn === DASHBOARD_ORIGIN) return;

  // eslint-disable-next-line no-console
  console.error(
    `[studio] Built for ${DASHBOARD_ORIGIN}, embedded in ${embeddedIn}. ` +
      "Every postMessage will be refused in both directions, and the editor " +
      "token request will time out with the host having heard nothing. " +
      "Rebuild this environment with VITE_DASHBOARD_ORIGIN=" +
      `${embeddedIn}`,
  );
}
